// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { concatStyleSets, ContextualMenu, DirectionalHint, Persona, PersonaSize, useTheme } from '@fluentui/react';
import { _formatString } from "../../../../acs-ui-common/src";
import React, { useMemo } from 'react';
import { chatMessageMenuStyle, menuIconStyleSet, menuItemIncreasedSizeStyles } from '../styles/ChatMessageComponent.styles';
/**
 * Chat message actions flyout that contains actions such as Edit Message, or Remove Message.
 *
 * @private
 */
export const ChatMessageActionFlyout = (props) => {
    var _a, _b;
    const theme = useTheme();
    const messageReadByCount = (_a = props.messageReadBy) === null || _a === void 0 ? void 0 : _a.length;
    const messageReadByList = (_b = props.messageReadBy) === null || _b === void 0 ? void 0 : _b.map(person => {
        const personaOptions = {
            hidePersonaDetails: true,
            size: PersonaSize.size24,
            text: person.displayName,
            styles: {
                root: {
                    margin: '0.25rem'
                }
            }
        };
        const { onRenderAvatar } = props;
        return {
            key: person.displayName,
            text: person.displayName,
            itemProps: {
                styles: props.increaseFlyoutItemSize ? menuItemIncreasedSizeStyles : undefined
            },
            onRenderIcon: () => { var _a; return onRenderAvatar ? onRenderAvatar((_a = person.id) !== null && _a !== void 0 ? _a : '', personaOptions) : React.createElement(Persona, Object.assign({}, personaOptions)); },
            iconProps: {
                styles: menuIconStyleSet
            }
        };
    });
    const menuItems = useMemo(() => {
        const items = [{
                key: 'Edit',
                text: props.strings.editMessage,
                itemProps: {
                    styles: props.increaseFlyoutItemSize ? menuItemIncreasedSizeStyles : undefined
                },
                iconProps: {
                    iconName: 'MessageEdit',
                    styles: menuIconStyleSet
                },
                onClick: props.onEditClick
            }, {
                key: 'Remove',
                text: props.strings.removeMessage,
                itemProps: {
                    styles: props.increaseFlyoutItemSize ? menuItemIncreasedSizeStyles : undefined
                },
                iconProps: {
                    iconName: 'MessageRemove',
                    styles: menuIconStyleSet
                },
                onClick: props.onRemoveClick
            }]; // only show read by x of x if more than 3 participants in total including myself
        // TODO: change strings.messageReadCount to be required if we can fallback to our own en-us strings for anything that Contoso doesn't provide
        if (props.remoteParticipantsCount && messageReadByCount !== undefined && props.remoteParticipantsCount >= 2 && props.showMessageStatus && props.strings.messageReadCount) {
            items.push({
                key: 'Read Count',
                text: _formatString(props.strings.messageReadCount, {
                    messageReadByCount: `${messageReadByCount}`,
                    remoteParticipantsCount: `${props.remoteParticipantsCount}`
                }),
                itemProps: {
                    styles: concatStyleSets({
                        linkContent: {
                            color: messageReadByCount > 0 ? theme.palette.neutralPrimary : theme.palette.neutralTertiary
                        },
                        root: {
                            borderTop: `1px solid ${theme.palette.neutralLighter}`
                        }
                    }, props.increaseFlyoutItemSize ? menuItemIncreasedSizeStyles : undefined)
                },
                calloutProps: preventUnwantedDismissProps,
                subMenuProps: {
                    items: messageReadByList !== null && messageReadByList !== void 0 ? messageReadByList : [],
                    calloutProps: preventUnwantedDismissProps
                },
                iconProps: {
                    iconName: 'MessageSeen',
                    styles: {
                        root: {
                            color: messageReadByCount > 0 ? theme.palette.themeDarkAlt : theme.palette.neutralTertiary
                        }
                    }
                },
                submenuIconProps: {
                    iconName: 'HorizontalGalleryRightButton',
                    styles: menuIconStyleSet
                },
                disabled: messageReadByCount <= 0
            });
        }
        return items;
    }, [props.strings.editMessage, props.strings.removeMessage, props.strings.messageReadCount, props.increaseFlyoutItemSize, props.onEditClick, props.onRemoveClick, props.remoteParticipantsCount, props.showMessageStatus, messageReadByCount, theme.palette.neutralPrimary, theme.palette.neutralTertiary, theme.palette.neutralLighter, theme.palette.themeDarkAlt, messageReadByList]); // gap space uses pixels
    return React.createElement(ContextualMenu, { alignTargetEdge: true, gapSpace: 5, isBeakVisible: false, items: menuItems, hidden: props.hidden, target: props.target, onDismiss: props.onDismiss, directionalHint: DirectionalHint.topRightEdge, className: chatMessageMenuStyle });
};
const preventUnwantedDismissProps = {
    // Disable dismiss on resize to work around a couple Fluent UI bugs
    // - The Callout is dismissed whenever *any child of window (inclusive)* is resized. In practice, this
    //   happens when we change the VideoGallery layout, or even when the video stream element is internally resized
    //   by the headless SDK.
    // - There is a `preventDismissOnEvent` prop that we could theoretically use to only dismiss when the target of
    //   of the 'resize' event is the window itself. But experimentation shows that setting that prop doesn't
    //   deterministically avoid dismissal.
    //
    // A side effect of this workaround is that the context menu stays open when window is resized, and may
    // get detached from original target visually. That bug is preferable to the bug when this value is not set -
    // The Callout (frequently) gets dismissed automatically.
    preventDismissOnResize: true
};
//# sourceMappingURL=ChatMessageActionsFlyout.js.map